import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  ListOrdered,
  FileText,
  Settings,
  Upload,
  Timer,
  Receipt,
  CalendarDays,
  GanttChartSquare,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/api/useAuth';

const menuItems = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: LayoutDashboard,
    requiresPermission: null,
  },
  {
    title: 'Kanban',
    url: '/kanban',
    icon: ClipboardList,
    requiresPermission: null,
  },
  {
    title: 'Pedidos',
    url: '/orders-tracking',
    icon: ListOrdered,
    requiresPermission: null,
  },
  {
    title: 'Calendário Obras',
    url: '/works-calendar',
    icon: CalendarDays,
    requiresPermission: null,
  },
  {
    title: 'Minhas Horas',
    url: '/my-hours',
    icon: Timer,
    requiresPermission: null,
  },
  {
    title: 'Importar Pedido',
    url: '/import',
    icon: Upload,
    requiresPermission: 'importOrders',
  },
  {
    title: 'Importar Faturamento',
    url: '/import-billing',
    icon: Receipt,
    requiresPermission: 'importOrders',
  },
  {
    title: 'Relatórios',
    url: '/reports',
    icon: FileText,
    requiresPermission: 'viewReports',
  },
  {
    title: 'Gantt Etapas',
    url: '/gantt',
    icon: GanttChartSquare,
    requiresPermission: 'viewReports',
  },
];

const adminItems = [
  {
    title: 'Administração',
    url: '/admin',
    icon: Settings,
    requiresAdmin: true,
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { user, hasPermission } = useAuth();

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const checkMenuPermission = (permissionKey: string | null): boolean => {
    if (!permissionKey) return true; // No permission required
    return hasPermission(permissionKey as keyof typeof user.profile.permissions);
  };

  const isAdmin = (): boolean => {
    return user?.profile.isAdmin === true;
  };

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems.filter(item => checkMenuPermission(item.requiresPermission));
  const filteredAdminItems = adminItems.filter(() => isAdmin());

  return (
    <Sidebar className="border-r border-sidebar-border">
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
            <img src="/metalmarc.png" alt="MetalMarc Logo" className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-sidebar-foreground">MetalProd</h2>
            <p className="text-xs text-sidebar-foreground/70">Gestão Industrial</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="w-4 h-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {filteredAdminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Sistema</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredAdminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)}>
                      <Link to={item.url}>
                        <item.icon className="w-4 h-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
